import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { AppShell } from '../components/Layout';
import { Banner, Loading } from '../components/ui';

const RULE_LABELS: Record<string, string> = {
  unit: 'What counts as one request',
  multiple_signers: 'Several signers',
  multiple_files: 'Several files',
  multiple_fields: 'Extra fields',
  reminders: 'Reminders',
  self_signing: 'Signing it yourself',
  voided_declined_expired: 'Voided, declined or expired',
  failed_dispatch: 'Failed delivery',
  replacement: 'Replacement agreements',
  api: 'API usage',
  reset: 'When the allowance resets',
  overage: 'Going over',
};

export function Pricing() {
  const [data, setData] = useState<any>(null);

  useEffect(() => { api.get('/pricing').then(setData).catch(() => undefined); }, []);

  return (
    <AppShell
      nav={[{ to: '/', label: 'Home' }, { to: '/blog', label: 'Blog' },
            { to: '/verify', label: 'Verify a document' }]}
      actions={<Link className="btn btn--primary btn--small" to="/register">Sign up</Link>}
    >
      <div className="shell section stack-l">
        <header className="stack-s">
          <h1>Pricing and allowances</h1>
          <p className="muted" style={{ maxWidth: '62ch' }}>
            Annual plans with a monthly signature-request allowance. Everything
            that affects what you are charged is stated here, at checkout, and in
            your dashboard — with the same numbers in all three places.
          </p>
        </header>

        {!data ? <Loading /> : (
          <>
            <div className="grid grid--cards">
              {data.plans.map((plan: any) => (
                <div className={`card pricecard${plan.plan_id === 'pro' ? ' pricecard--featured' : ''}`}
                     key={plan.plan_id}>
                  <div>
                    <h2 style={{ margin: 0 }}>{plan.plan_name}</h2>
                    <div className="price">
                      {plan.price_display.replace('USD ', '')}
                      <span className="price__unit"> / year</span>
                    </div>
                  </div>
                  <ul className="plan-features">
                    <li>{plan.monthly_requests} signature requests per subscription month</li>
                    <li>Unlimited self-signing</li>
                    <li>Up to 10 signers per request</li>
                    <li>Evidence export at any time</li>
                    <li className={plan.api_access ? '' : 'no'}>
                      {plan.api_access ? 'API access, webhooks and embedded signing'
                                       : 'No API access'}
                    </li>
                  </ul>
                  <span className="spacer" />
                  <Link className={`btn btn--block${plan.plan_id === 'pro' ? ' btn--primary' : ''}`}
                        to="/register">Start the {plan.trial_days}-day trial</Link>
                </div>
              ))}

              {/* Shown beside the plans but priced differently on purpose:
                  nothing recurs and nothing expires. */}
              <div className="card pricecard" key="credits">
                <div>
                  <h2 style={{ margin: 0 }}>{data.credits.plan_name}</h2>
                  <div className="price">
                    {data.credits.price_display.replace('USD ', '')}
                    <span className="price__unit"> / {data.credits.credits} credits</span>
                  </div>
                </div>
                <ul className="plan-features">
                  <li>
                    {data.credits.credits_per_send} credits per send —{' '}
                    {data.credits.sends_per_pack} sends per pack
                  </li>
                  <li><strong>Credits do not expire</strong></li>
                  <li>Unlimited self-signing</li>
                  <li>No subscription, nothing renews</li>
                  <li>Optional automatic top-up</li>
                  <li className="no">No API access</li>
                </ul>
                <span className="spacer" />
                <Link className="btn btn--block" to="/register">Pay as you go</Link>
              </div>
            </div>

            {/* No seal banner and no seal row in the plan cards.
                *
                * While sealing is unapplied the plans simply do not offer it,
                * so there is no claim here needing a caveat. When Phase 2
                * ships, the row goes back -- without one.
                *
                * The "Assurance level" section below stays. Its text comes
                * from the API and describes what an agreement actually
                * carries, which is a different statement from what a plan
                * includes, and the one that must never be softened. */}

            <Banner tone="warn" title="Before you start the trial">
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                <li>A valid card is required to activate the sending trial. Saving a
                    card does not start it — activation is a separate, explicit step.</li>
                <li>The trial lasts {data.plans[0].trial_days} days and includes
                    {' '}{data.plans[0].trial_requests} signature requests.</li>
                <li>At the end of the trial you are charged the plan price and the
                    subscription renews annually until canceled.</li>
                <li>Billing begins when the trial expires unless you cancel first —
                    even if your recipients have not finished signing.</li>
                <li>{data.plans[0].tax_treatment}</li>
              </ul>
            </Banner>

            <section className="card stack">
              <h2 style={{ margin: 0 }}>How requests are counted</h2>
              <div className="kv">
                {Object.entries(data.allowance_rules).map(([key, value]) => (
                  <div key={key} style={{ display: 'contents' }}>
                    <div className="kv__k">{RULE_LABELS[key] || key}</div>
                    <div className="kv__v">{String(value)}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="card stack">
              <h2 style={{ margin: 0 }}>Cancelling</h2>
              <p className="muted" style={{ margin: 0 }}>
                Cancelling disables renewal. It does not delete completed
                agreements and does not end your ability to retrieve and export
                them. Retention is covered in the{' '}
                <Link to="/terms#retention">Terms of Service</Link>.
              </p>
            </section>

            <section className="card stack">
              <h2 style={{ margin: 0 }}>Assurance level</h2>
              <p className="muted" style={{ margin: 0 }}>{data.assurance.note}</p>
            </section>
          </>
        )}
      </div>
    </AppShell>
  );
}
